import { useState } from 'react';
import { X, Facebook, Twitter, Linkedin, Youtube, Github, Twitch, MessageCircle } from 'lucide-react';

interface SocialMediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (platform: string, handle: string) => void;
}

const socialPlatforms = [
  { name: 'Facebook', icon: Facebook, color: 'hover:bg-amber-50 hover:border-amber-300' },
  { name: 'Twitter', icon: Twitter, color: 'hover:bg-sky-50 hover:border-sky-300' },
  { name: 'LinkedIn', icon: Linkedin, color: 'hover:bg-amber-50 hover:border-amber-400' },
  { name: 'YouTube', icon: Youtube, color: 'hover:bg-red-50 hover:border-red-300' },
  { name: 'GitHub', icon: Github, color: 'hover:bg-gray-50 hover:border-gray-400' },
  { name: 'Twitch', icon: Twitch, color: 'hover:bg-purple-50 hover:border-purple-300' },
  { name: 'TikTok', icon: MessageCircle, color: 'hover:bg-pink-50 hover:border-pink-300' },
];

export default function SocialMediaModal({ isOpen, onClose, onAdd }: SocialMediaModalProps) {
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [handle, setHandle] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (selectedPlatform && handle.trim()) {
      onAdd(selectedPlatform, handle.trim());
      setSelectedPlatform(null);
      setHandle('');
      onClose();
    }
  };

  const handlePlatformSelect = (platform: string) => {
    setSelectedPlatform(platform);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden animate-slideUp">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h3 className="text-xl font-semibold text-gray-800">Add Social Media</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {!selectedPlatform ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">Select a platform</p>
              {socialPlatforms.map((platform) => {
                const Icon = platform.icon;
                return (
                  <button
                    key={platform.name}
                    onClick={() => handlePlatformSelect(platform.name)}
                    className={`w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl transition-all ${platform.color}`}
                  >
                    <Icon className="w-5 h-5 text-gray-700" />
                    <span className="font-medium text-gray-800">{platform.name}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4 animate-fadeIn">
              <button
                onClick={() => setSelectedPlatform(null)}
                className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
              >
                ← Back to platforms
              </button>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {selectedPlatform} Handle
                </label>
                <input
                  type="text"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder="@username"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all text-gray-800"
                  autoFocus
                />
              </div>
              <button
                onClick={handleSubmit}
                disabled={!handle.trim()}
                className="w-full bg-gradient-to-r from-amber-600 to-amber-500 text-white py-3 rounded-xl font-medium hover:from-amber-700 hover:to-amber-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-amber-500/30"
              >
                Add Handle
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
